import { Badge, BlockStack, Button, Card, Checkbox, FormLayout, InlineStack, Page, Select, Tag, Text, Toast } from "@shopify/polaris";
import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useAppFormik } from "../contexts/FormikContext";
import { createSettingAsync, getAllSetting, getSettingAsync } from "../redux/slice/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import RichTextEditor from "../components/RichTextEditor";
import ContextSaveBar from "../components/ContextSaveBar";
import { Country } from "country-state-city";

const GeoBlockingPage = () => {
    const { values, setFieldValue, setValues, initialValues, resetForm } = useAppFormik();
    const dispatch = useDispatch();
    const { isCreateSettingLoading } = useSelector(getAllSetting);
    // Ensure selectedCountry is always an array (handle legacy string format)
    const selectedCountryValue = values.geoBlocking?.selectedCountry;
    const selectedCountries = Array.isArray(selectedCountryValue)
        ? selectedCountryValue
        : (selectedCountryValue ? [selectedCountryValue] : []);
    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const toastMarkup = toastActive ? <Toast content={toastMessage} onDismiss={() => setToastActive(false)} /> : null;

    // Helper function to mark changes when user interacts with fields
    const markAsChanged = () => {
        setHasUnsavedChanges(true);
    };

    // Get all countries from country-state-city package
    const countries = useMemo(() => {
        return Country.getAllCountries().map((country) => ({
            label: country.name,
            value: country.isoCode.toLowerCase(),
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, []);

    const handleCountryAdd = (countryValue) => {
        if (countryValue) {
            // Normalize to lowercase for consistent storage
            const normalizedCode = countryValue.toLowerCase();
            // Check if country code already exists (case-insensitive)
            const exists = selectedCountries.some(c => c.toLowerCase() === normalizedCode);
            if (!exists) {
                const updatedCountries = [...selectedCountries, normalizedCode];
                setFieldValue("geoBlocking.selectedCountry", updatedCountries);
                markAsChanged();
            }
        }
    };

    const handleCountryRemove = (countryValue) => {
        const updatedCountries = selectedCountries.filter((country) => country !== countryValue);
        setFieldValue("geoBlocking.selectedCountry", updatedCountries);
        markAsChanged();
    };

    // Normalize selectedCountry to always be an array (handle legacy string format from API)
    useEffect(() => {
        const currentValue = values.geoBlocking?.selectedCountry;
        if (currentValue !== undefined && currentValue !== null) {
            if (!Array.isArray(currentValue)) {
                // Convert string to array if it's the old format
                const normalizedValue = currentValue ? [currentValue] : [];
                setFieldValue("geoBlocking.selectedCountry", normalizedValue);
            }
        }
    }, [values.geoBlocking?.selectedCountry, setFieldValue]);

    const handleSave = async () => {
        await dispatch(createSettingAsync({ payload: { settings: values }, callback: handleCallBack }));
    };

    const handleCallBack = () => {
        dispatch(getSettingAsync({}));
        showToast("Geo Blocking Rules Save Successfully!");
        setHasUnsavedChanges(false);
    };

    const handleDiscard = () => {
        // Reset form to initial values using Formik's resetForm
        resetForm({ values: initialValues });
        setHasUnsavedChanges(false);
        showToast("Changes discarded");
    };

    return (
        <Page title="Geo Blocking">
            <BlockStack gap="400">
                <Card>
                    <BlockStack gap="400">
                        <InlineStack gap="300" blockAlign="center" align="start">
                            <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/Geo-Blocking.png?v=1766827965" alt="Geo Blocking" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                            <BlockStack gap='050'>
                                <Text variant="headingMd">Manage Country Wise Blocking</Text>
                                <Text tone="subdued">
                                    Select multiple countries to block access to your store.
                                </Text>
                            </BlockStack>
                        </InlineStack>
                        <FormLayout.Group condensed>
                            <Select
                                label="Select Country"
                                options={[
                                    { label: "Select a country", value: "" },
                                    ...countries.filter((country) => !selectedCountries.includes(country.value))
                                ]}
                                value=""
                                onChange={(value) => {
                                    if (value) {
                                        handleCountryAdd(value);
                                    }
                                }}
                            />
                        </FormLayout.Group>
                    </BlockStack>

                    <br />

                    {selectedCountries.length > 0 && (
                        <BlockStack gap="400">
                            <Text variant="headingMd">Selected Countries</Text>

                            <InlineStack gap="200" wrap>
                                {selectedCountries.map((countryValue) => {
                                    // Normalize country code to lowercase for comparison
                                    const normalizedCode = countryValue?.toLowerCase();
                                    const country = countries.find((c) => c.value.toLowerCase() === normalizedCode);
                                    // Display country name in Tag, but keep code in metafield
                                    return (
                                        <Tag
                                            key={countryValue}
                                            onRemove={() => handleCountryRemove(countryValue)}
                                        >
                                            {country?.label || countryValue}
                                        </Tag>
                                    );
                                })}
                            </InlineStack>

                            <BlockStack gap="400">
                                <Checkbox
                                    label="Blocking Message"
                                    checked={values.geoBlocking?.showBlockingMessage || false}
                                    onChange={(val) => {
                                        setFieldValue(`geoBlocking.showBlockingMessage`, val);
                                        markAsChanged();
                                    }}
                                />
                                {values.geoBlocking?.showBlockingMessage && (
                                    <RichTextEditor
                                        label="Blocking Rules Message"
                                        value={values.geoBlocking?.blockingMessage || ""}
                                        onChange={(val) => {
                                            setFieldValue(`geoBlocking.blockingMessage`, val);
                                            markAsChanged();
                                        }}
                                    />
                                )}
                            </BlockStack>
                        </BlockStack>
                    )}
                </Card>
            </BlockStack>
            {toastMarkup}
            {hasUnsavedChanges && <ContextSaveBar
                message="You have unsaved changes"
                saveAction={{
                    content: "Save",
                    onAction: handleSave,
                    loading: isCreateSettingLoading,
                    disabled: isCreateSettingLoading
                }}
                discardAction={{
                    content: "Discard",
                    onAction: handleDiscard,
                    disabled: isCreateSettingLoading
                }}
                visible={hasUnsavedChanges}
            />}
        </Page>
    )
};

export default GeoBlockingPage;